import { request } from '../api';
import { HTTP_METHODS } from '../project-types/common/http-types';

export interface RecoveryPlan {
  initialTake: number;
  regularTake: number;
  period: string;
  noPayments: number;
  firstCollectionDate: string;
}

export interface PreviewRow { date: string; extra: number; usual: number; total: number }

export interface DecisionPreview {
  liveArrears: number;
  snapshotArrears: number;
  stale: boolean;
  students: number;
  planEnded: boolean;
  sendable: boolean;
  dateAdjusted?: boolean;
  plan: RecoveryPlan | null;
  rows: PreviewRow[];
  totalRecovered: number;
  firstCollectionDate: string | null;
  lastCollectionDate: string | null;
}

export interface ArrearsDecisionSummary {
  id: number;
  subscriptionId: number;
  parentId: number;
  parentName: string;
  parentEmail: string;
  studentCodes: string[];
  option: string;
  optionLabel: string;
  arrearsAmount: number;
  parentMessage: string | null;
  proposedPlan: RecoveryPlan | null;
  status: string;
  decidedAt: string | null;
  decidedBy: string | null;
  note: string | null;
  recoverySubscriptionId: number | null;
  createdAt: string;
  daysWaiting: number | null;
  usualAmount: number;
  usualPeriod: string;
  collectionsDone: number;
  collectionsPlanned: number;
}

export interface MutationResult {
  decision: ArrearsDecisionSummary;
  recoverySubscriptionId?: number;
  error?: string;
}

const MUTATION_STATUSES = [200, 409, 422];

// `request` resolves the axios error object for allowed non-2xx statuses, so the JSON body may sit
// under `response.response.data` rather than `response.data`.
const payloadOf = (response: any) => response?.data ?? response?.response?.data;

// 404 means the feature is not enabled for this campaign (dark deploy or campaign not opted in): an empty queue.
export const fetchDecisions = async (campaignId: string, status: 'pending' | 'all' = 'pending'): Promise<ArrearsDecisionSummary[]> => {
  const response = await request(
    { url: `/oto-schemes/${campaignId}/arrears-decisions`, method: HTTP_METHODS.GET, params: { status } },
    [200, 404],
  );
  if (response?.response?.status === 404) return [];
  return response?.data?.decisions || [];
};

export const fetchDecision = async (id: number | string): Promise<{ decision: ArrearsDecisionSummary; preview: DecisionPreview }> => {
  const response = await request({ url: `/arrears-decisions/${id}`, method: HTTP_METHODS.GET });
  return response.data;
};

export const previewDecision = async (id: number | string, plan: RecoveryPlan): Promise<DecisionPreview> => {
  const response = await request({ url: `/arrears-decisions/${id}/preview`, method: HTTP_METHODS.POST, data: { plan } });
  return response.data.preview;
};

export const approveDecision = async (id: number | string, plan?: RecoveryPlan): Promise<MutationResult> => {
  const response = await request(
    { url: `/arrears-decisions/${id}/approve`, method: HTTP_METHODS.POST, data: plan ? { plan } : {} },
    MUTATION_STATUSES,
  );
  return payloadOf(response) as MutationResult;
};

export const declineDecision = async (id: number | string, note: string): Promise<MutationResult> => {
  const response = await request({ url: `/arrears-decisions/${id}/decline`, method: HTTP_METHODS.POST, data: { note } }, MUTATION_STATUSES);
  return payloadOf(response) as MutationResult;
};

export const reviewDecision = async (id: number | string, note?: string): Promise<MutationResult> => {
  const response = await request({ url: `/arrears-decisions/${id}/review`, method: HTTP_METHODS.POST, data: { note } }, MUTATION_STATUSES);
  return payloadOf(response) as MutationResult;
};
